import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatDuration } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Plus, Trash2 } from "lucide-react";

const EMPTY = { name: "", category: "Cils", duration: 60, price: 50, description: "", deposit_mode: "default", deposit_value: "" };

const DEPOSIT_MODES = [
  ["default", "Acompte par défaut (réglage global)"],
  ["fixed", "Montant fixe (€)"],
  ["percent", "Pourcentage du prix (%)"],
  ["none", "Aucun acompte"],
];

export default function ServicesPanel() {
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/services").then((res) => setCategories(res.data.categories)).catch(() => {});
  useEffect(() => { load(); }, []);

  const categoryNames = [...new Set(categories.map((c) => c.name))];

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        category: editing.category,
        duration: Number(editing.duration),
        price: Number(editing.price),
        description: editing.description,
        deposit_mode: editing.deposit_mode || "default",
        deposit_value: editing.deposit_value === "" || editing.deposit_value == null ? null : Number(editing.deposit_value),
      };
      if (editing.id) await api.patch(`/admin/services/${editing.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success(editing.id ? "Prestation modifiée" : "Prestation ajoutée");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer « ${s.name} » ?`)) return;
    await api.delete(`/admin/services/${s.id}`).catch(() => toast.error("Suppression impossible"));
    toast.success("Prestation supprimée");
    load();
  };

  const inputCls = "mt-2 h-12 rounded-none border-black/10 bg-transparent font-light";

  return (
    <div className="mt-10" data-testid="services-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-3xl text-[var(--brand-dark)]">Prestations & tarifs</h2>
        <button onClick={() => setEditing({ ...EMPTY })}
                className="flex items-center gap-2 rounded-full bg-[var(--brand-dark)] px-6 py-3 text-xs uppercase tracking-[0.2em] text-[var(--brand-bg)] transition-transform duration-200 hover:scale-95"
                data-testid="service-add-btn">
          <Plus size={14} /> Ajouter
        </button>
      </div>

      {categories.map((cat) => (
        <div key={cat.name} className="mt-8 border border-black/5 bg-[var(--brand-bg)]" data-testid={`services-cat-${cat.name}`}>
          <p className="border-b border-black/5 px-6 py-4 text-xs uppercase tracking-[0.25em] text-[var(--brand-accent)]">{cat.name}</p>
          {cat.services.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-4 border-b border-black/5 px-6 py-4 last:border-0" data-testid={`service-row-${s.id}`}>
              <p className="min-w-40 flex-1 font-light text-[var(--brand-dark)]">{s.name}</p>
              <p className="text-sm font-light text-[var(--brand-muted)]">{formatDuration(s.duration)}</p>
              <p className="font-display text-xl text-[var(--brand-dark)]">{s.price} €</p>
              {(s.deposit_amount || 0) > 0 && (
                <p className="rounded-full bg-[var(--brand-accent)]/15 px-3 py-1 text-[11px] uppercase tracking-[0.12em] text-[var(--brand-dark)]" data-testid={`service-deposit-${s.id}`}>
                  Acompte {s.deposit_amount} €
                </p>
              )}
              <button onClick={() => setEditing({ ...s, deposit_value: s.deposit_value ?? "" })} className="flex items-center gap-1.5 rounded-full border border-black/15 px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-[var(--brand-dark)] transition-colors hover:border-[var(--brand-accent)]" data-testid={`service-edit-${s.id}`}>
                <Pencil size={12} /> Modifier
              </button>
              <button onClick={() => remove(s)} className="flex items-center gap-1.5 rounded-full border border-[#993333]/30 px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-[#993333] transition-colors hover:bg-[#993333] hover:text-white" data-testid={`service-delete-${s.id}`}>
                <Trash2 size={12} /> Supprimer
              </button>
            </div>
          ))}
        </div>
      ))}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="bg-[var(--brand-bg)] sm:max-w-lg" data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-normal text-[var(--brand-dark)]">
              {editing?.id ? "Modifier la prestation" : "Nouvelle prestation"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={save} className="grid gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">Nom</label>
                <Input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls} data-testid="service-form-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">Catégorie</label>
                <Input required list="service-categories" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={inputCls} data-testid="service-form-category" />
                <datalist id="service-categories">
                  {categoryNames.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">Prix (€)</label>
                  <Input required type="number" min="0" step="0.5" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className={inputCls} data-testid="service-form-price" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">Durée (min)</label>
                  <Input required type="number" min="15" step="15" value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} className={inputCls} data-testid="service-form-duration" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">Acompte</label>
                  <Select value={editing.deposit_mode || "default"} onValueChange={(v) => setEditing({ ...editing, deposit_mode: v })}>
                    <SelectTrigger className="mt-2 h-12 rounded-none border-black/10 bg-transparent font-light" data-testid="service-form-deposit-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPOSIT_MODES.map(([v, label]) => (
                        <SelectItem key={v} value={v} data-testid={`service-form-deposit-${v}`}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {(editing.deposit_mode === "fixed" || editing.deposit_mode === "percent") && (
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">
                      {editing.deposit_mode === "percent" ? "Pourcentage (%)" : "Montant (€)"}
                    </label>
                    <Input required type="number" min="1" value={editing.deposit_value}
                           onChange={(e) => setEditing({ ...editing, deposit_value: e.target.value })}
                           className={inputCls} data-testid="service-form-deposit-value" />
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">Description</label>
                <Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-2 rounded-none border-black/10 bg-transparent font-light" data-testid="service-form-description" />
              </div>
              <button type="submit" disabled={saving}
                      className="mt-2 rounded-full bg-[var(--brand-dark)] py-4 text-xs uppercase tracking-[0.25em] text-[var(--brand-bg)] transition-transform duration-200 hover:scale-[0.98] disabled:opacity-50"
                      data-testid="service-form-save">
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
